import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CheckCircle, 
  CreditCard, 
  DollarSign, 
  Clock,
  ArrowRight,
  Shield,
  Star
} from 'lucide-react';
import { toast } from "sonner";
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('usd');
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    fetchCurrencies();
  }, []);

  useEffect(() => {
    fetchPackages();
  }, [selectedCurrency]);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/payments/currencies`);
      setCurrencies(response.data.currencies);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/payments/packages?currency=${selectedCurrency}`);
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching payment packages:', error);
      toast.error('Error al cargar los paquetes de pago');
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = () => {
    const currency = currencies.find(c => c.code === selectedCurrency);
    return currency ? currency.symbol : '$';
  };

  const initiatePayment = async (packageId) => {
    try {
      setPaymentLoading(packageId);
      
      // Get current origin URL
      const originUrl = window.location.origin;
      
      const paymentData = {
        package_id: packageId,
        customer_email: 'cliente@ejemplo.com', // This would come from a form or user context
        customer_name: 'Cliente Ejemplo',
        origin_url: originUrl,
        metadata: {
          source: 'website_packages',
          package_selection: packageId
        }
      };

      const response = await axios.post(`${API}/payments/checkout/session`, paymentData);
      
      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No se recibió la URL de pago');
      }
      
    } catch (error) {
      console.error('Error creating payment session:', error);
      toast.error('Error al procesar el pago. Inténtalo de nuevo.');
    } finally {
      setPaymentLoading(null);
    }
  };

  const getPopularBadge = (packageId) => {
    return packageId.includes('online_design_standard') ? (
      <Badge className="absolute -top-3 -right-3 bg-red-600 text-white px-4 py-1">
        <Star size={14} className="mr-1" />
        Más Popular
      </Badge>
    ) : null;
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-64 mx-auto"></div>
              <div className="h-4 bg-gray-200 rounded w-96 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="paquetes" className="py-20 bg-gradient-to-br from-slate-50 via-white to-red-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-6 bg-red-100 text-red-800 border-red-200 px-4 py-2">
            Paquetes de Diseño Online
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Paga por Tu
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-700"> Proyecto Online</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Elige el paquete de diseño de planos online que mejor se adapte a tu proyecto. Pago 100% seguro y entrega digital.
          </p>
          
          {/* Currency Selector */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-2 flex items-center">
              <span className="text-slate-700 font-medium mr-3 px-3">Moneda:</span>
              <div className="flex gap-2">
                {currencies.map((currency) => (
                  <button
                    key={currency.code}
                    onClick={() => setSelectedCurrency(currency.code)}
                    className={`px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                      selectedCurrency === currency.code
                        ? 'bg-red-600 text-white shadow-md'
                        : 'text-slate-600 hover:bg-red-50 hover:text-red-600'
                    }`}
                  >
                    {currency.symbol} {currency.code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Security Badge */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-lg border border-slate-200">
            <Shield size={20} className="text-red-600" />
            <span className="text-sm font-medium text-slate-700">Pagos 100% seguros con Stripe</span>
            <div className="flex gap-1">
              <div className="w-8 h-5 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">VISA</div>
              <div className="w-8 h-5 bg-red-500 rounded text-white text-xs flex items-center justify-center font-bold">MC</div>
            </div>
          </div>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg, index) => (
            <Card 
              key={pkg.id} 
              className={`relative border-0 shadow-xl bg-white hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group ${
                pkg.id.includes('online_design_standard') ? 'ring-2 ring-red-500 scale-105' : ''
              }`}
            >
              {getPopularBadge(pkg.id)}
              
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <DollarSign size={32} className="text-red-600" />
                </div>
                <CardTitle className="text-xl text-slate-900 group-hover:text-red-600 transition-colors duration-300">
                  {pkg.name}
                </CardTitle>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <p className="text-slate-600 text-center leading-relaxed">
                  {pkg.description}
                </p>
                
                {/* Price */}
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600 mb-1">
                    {getCurrencySymbol()}{pkg.amount.toFixed(0)}
                  </div>
                  <div className="text-sm text-slate-500">{pkg.currency.toUpperCase()}</div>
                </div>
                
                {/* Features */}
                <div className="space-y-3">
                  {pkg.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start gap-3 text-sm">
                      <CheckCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
                
                {/* Payment Button */}
                <Button 
                  data-testid={`pay-package-${pkg.id}`}
                  onClick={() => initiatePayment(pkg.id)}
                  disabled={paymentLoading === pkg.id}
                  className={`w-full py-3 text-white group-hover:scale-105 transition-all duration-300 ${
                    pkg.id.includes('online_design_standard') 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-slate-700 hover:bg-slate-800'
                  }`}
                >
                  {paymentLoading === pkg.id ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Procesando...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CreditCard size={16} />
                      Pagar Ahora
                      <ArrowRight size={16} />
                    </div>
                  )}
                </Button>
                
                {/* Time estimate */}
                <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
                  <Clock size={14} />
                  <span>Pago procesado en 2-3 minutos</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 bg-white rounded-3xl p-8 shadow-lg">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">
              ¿Tienes Dudas sobre los Pagos?
            </h3>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
              Todos nuestros pagos son procesados de forma segura a través de Stripe. 
              Aceptamos todas las tarjetas principales y transferencias bancarias.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <Shield size={32} className="text-red-600 mx-auto mb-3" />
                <h4 className="font-semibold text-slate-900 mb-2">100% Seguro</h4>
                <p className="text-sm text-slate-600">Encriptación SSL y PCI DSS</p>
              </div>
              <div className="text-center">
                <Clock size={32} className="text-red-600 mx-auto mb-3" />
                <h4 className="font-semibold text-slate-900 mb-2">Procesamiento Rápido</h4>
                <p className="text-sm text-slate-600">Confirmación inmediata</p>
              </div>
              <div className="text-center">
                <CheckCircle size={32} className="text-red-600 mx-auto mb-3" />
                <h4 className="font-semibold text-slate-900 mb-2">Garantía</h4>
                <p className="text-sm text-slate-600">Satisfacción garantizada</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PaymentPackages;